import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { LayoutGrid } from 'lucide-react';
import type { FeatureUsageItem, ProcessAdoptionItem } from '../../../lib/api';
import {
  AREA_LABELS,
  USAGE_STATUS_COLOR,
  USAGE_STATUS_LABEL,
} from '../platformAnalyticsConfig';
import { ChartEmptyState } from './ChartEmptyState';

interface AreasTabProps {
  adoption: ProcessAdoptionItem[];
  features: FeatureUsageItem[];
}

export function AreasTab({ adoption, features }: AreasTabProps) {
  const adoptionChart = adoption
    .filter((a) => a.companies_using > 0 || a.adoption_percentage > 0)
    .map((a) => ({
      area: AREA_LABELS[a.feature] || a.feature,
      organisations: a.companies_using,
      pct: a.adoption_percentage,
      status: a.status,
    }))
    .sort((a, b) => b.organisations - a.organisations);

  const usageChart = features
    .filter((f) => AREA_LABELS[f.feature])
    .slice(0, 10)
    .map((f) => ({
      area: AREA_LABELS[f.feature] || f.feature,
      people: f.unique_users,
      visits: f.requests,
    }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Organisations using each area</CardTitle>
            <CardDescription>How many companies have visited each part of the portal.</CardDescription>
          </CardHeader>
          <CardContent>
            {adoptionChart.length === 0 ? (
              <ChartEmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={adoptionChart} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="area" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => [v, 'Organisations']} />
                  <Bar dataKey="organisations" radius={[0, 4, 4, 0]}>
                    {adoptionChart.map((entry, i) => (
                      <Cell key={i} fill={USAGE_STATUS_COLOR[entry.status] || '#6366f1'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">People using each area</CardTitle>
            <CardDescription>Unique users who opened each section during the period.</CardDescription>
          </CardHeader>
          <CardContent>
            {usageChart.length === 0 ? (
              <ChartEmptyState />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={usageChart} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="area" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                  <Tooltip />
                  <Bar dataKey="people" fill="#6366f1" name="People" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <LayoutGrid className="h-5 w-5" />
            Area usage summary
          </CardTitle>
          <CardDescription>Plain-language view of which portal sections are getting traction.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {adoption.map((item) => (
              <div key={item.feature} className="space-y-1">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">{AREA_LABELS[item.feature] || item.feature}</span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {item.companies_using} of {item.total_companies} orgs · {USAGE_STATUS_LABEL[item.status]}
                  </span>
                </div>
                <div className="bg-muted rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(item.adoption_percentage, item.companies_using > 0 ? 4 : 0)}%`,
                      backgroundColor: USAGE_STATUS_COLOR[item.status],
                    }}
                  />
                </div>
              </div>
            ))}
            {adoption.length === 0 && <ChartEmptyState />}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
